import { Activity, FunctionComponent, memo, RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import styles from '../custom-select.module.css';

import { TReactSelectOption } from '../../react-select/custom-react-select.types';
import Text from '@ui/text/text/text';
import useTrackBoundingClient from '@/utils/use-track-bounding-client';

type Props = {
  selectedOptions: TReactSelectOption[];
  selectRef: RefObject<HTMLElement | null>;
  lockActiveState: boolean;
  RenderUnfocusedLabelContent?: FunctionComponent<{
    option: TReactSelectOption;
    idx: number;
  }>;
};

const RUI_GAP = 24;
const RUI_MORE_WIDTH = 50;
const RUI_MORE_WIDTH_H_PADDING = 8;

const CustomSelectUnfocusedItems = memo(
  ({ lockActiveState, selectRef, selectedOptions, RenderUnfocusedLabelContent }: Props) => {
    const refs = useRef<Array<HTMLElement | null>>(Array.from({ length: selectedOptions.length }));

    useEffect(() => {
      refs.current = Array.from({ length: selectedOptions.length });
    }, [selectedOptions.length]);

    const containerRect = useTrackBoundingClient({
      ref: selectRef,
      trackOnWindowResize: true,
      open: !!selectedOptions.length,
      rerunCounter: lockActiveState ? 1 : 0,
    });

    const containerWidth = containerRect?.width || 0;

    const labelHeight = useMemo(() => {
      const labelEl = selectRef?.current?.querySelector(`.${styles['label']}`);
      if (!labelEl) {
        return '100%';
      }
      return `${labelEl.getBoundingClientRect().height}px`;
    }, [selectRef]);

    const remainingWidth = useRef(containerWidth);
    const hasReachedOverflow = useRef<null | number>(null);

    const calculateAndApplyItemWidths = useCallback(
      (idx: number) => {
        const el = refs.current[idx];
        if (!el) return;

        el.style.minWidth = 'max-content';
        if (idx === 0) {
          hasReachedOverflow.current = null;
          remainingWidth.current = containerWidth;
        }

        let moreWidth = RUI_MORE_WIDTH + RUI_MORE_WIDTH_H_PADDING * 2;
        /* readabilityOffset means if there's at least this much space at the end, then we will show a truncated option */
        const readabilityOffset = 40;

        const elWidth = el.getBoundingClientRect().width;
        if (elWidth + RUI_GAP + readabilityOffset > remainingWidth.current - moreWidth) {
          if (idx === selectedOptions.length - 1) {
            moreWidth = 0;
          }
          el.style.maxWidth = `${remainingWidth.current - moreWidth}px`;
          el.style.minWidth = `${remainingWidth.current - moreWidth}px`;
          el.style.width = `${remainingWidth.current - moreWidth}px`;

          if (hasReachedOverflow.current !== null && idx > hasReachedOverflow.current) {
            el.style.display = 'none';
          }

          if (hasReachedOverflow.current === null) {
            hasReachedOverflow.current = idx;
          }
        }
        remainingWidth.current = remainingWidth.current - elWidth - RUI_GAP;
      },
      [containerWidth, selectedOptions.length]
    );

    if (!containerWidth || !selectedOptions.length) return null;
    return (
      <Activity mode={lockActiveState ? 'hidden' : 'visible'}>
        <div
          key={selectedOptions.length}
          data-component="CustomSelectUnfocusedItems"
          style={{
            width: `${selectRef?.current?.clientWidth || 0}px`,
            minWidth: `${selectRef?.current?.clientWidth || 0}px`,
            maxWidth: `${selectRef?.current?.clientWidth || 0}px`,
            height: labelHeight,
          }}
          onPointerDown={(e) => {
            e.preventDefault();
          }}
          className={`z-1 overflow-hidden top-0 left-0 position-absolute flex ai-center jc-flex-start gap-${RUI_GAP}`}
        >
          {selectedOptions.map((o, idx) => {
            return (
              <Text
                key={`${o.value} ${containerRect?.width || 0}`}
                clamp={1}
                whiteSpace="normal"
                width="fit-content"
                height={RenderUnfocusedLabelContent ? '100%' : undefined}
                minWidth="max-content"
                wordBreak="break-all"
                className={styles.unfocusedSelectedOption}
                maxWidth={`${containerWidth}px`}
                ref={(r) => {
                  if (!(r instanceof HTMLElement)) return;

                  refs.current[idx] = r;

                  calculateAndApplyItemWidths(idx);
                }}
              >
                {RenderUnfocusedLabelContent ? <RenderUnfocusedLabelContent option={o} idx={idx} /> : o.label}
              </Text>
            );
          })}

          <div
            className={`flex pc-center ${styles.moreChip}`}
            style={{
              width: `${RUI_MORE_WIDTH}px`,
              padding: `3px ${RUI_MORE_WIDTH_H_PADDING}px`,
              display:
                hasReachedOverflow.current === null || hasReachedOverflow.current === selectedOptions.length - 1
                  ? 'none'
                  : 'block',
            }}
          >
            <Text color="var(--local-chip-input-default-icotext)" className="L2 text-center" whiteSpace="nowrap">
              + {selectedOptions.length - 1 - (hasReachedOverflow.current || 0)}
            </Text>
          </div>
        </div>
      </Activity>
    );
  }
);

CustomSelectUnfocusedItems.displayName = 'CustomSelectUnfocusedItems';

export default CustomSelectUnfocusedItems;
